import { useQuery } from '@tanstack/react-query'
import { getApiClient, ApiProblemError } from '@/data/apiClient'
import { useActiveOrgId } from '@/data/store'
import type { components } from '@/api/generated/schema'
import type { Problem } from '@/data/apiClient'

type WorkspaceSummary = components['schemas']['WorkspaceSummary']

/**
 * Shared by all three `destination`-role panes (W155, closing the W146 gap `RestoreWizardLayer.tsx`'s
 * own doc comment named: "cross-workspace restore is not built this pass... no picker anywhere in
 * the 33 pane specs"). A real `<select>` over `organisationListWorkspaces`, defaulting to the
 * workspace the restore was captured from — picking a different one is what makes
 * `RestoreInput.destination_workspace_id` ever carry anything but `undefined`, which is what makes
 * `v-remap` reachable from a live flow at all.
 */
export function DestinationWorkspacePicker({
  currentWorkspaceId,
  value,
  onChange,
}: {
  currentWorkspaceId: string
  value: string
  onChange: (workspaceId: string, workspaceName: string) => void
}) {
  const orgId = useActiveOrgId()!

  const workspaces = useQuery({
    queryKey: ['workspaces', orgId],
    // Found live, 2026-09-25 (systemic audit after VHealth.tsx/VDrift.tsx's own fix) — see that
    // fix's own doc comment for the mount-time/org-switch race this guards against.
    enabled: Boolean(orgId),
    queryFn: async () => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces', { params: { path: { org_id: orgId } } })
      if (error) throw new ApiProblemError(error as Problem)
      return (data.items ?? []) as unknown as readonly WorkspaceSummary[]
    },
  })

  return (
    <select
      value={value}
      onChange={(e) => {
        const w = workspaces.data?.find((w) => w.workspace_id === e.target.value)
        onChange(e.target.value, w?.name ?? e.target.value)
      }}
      className="w-full border border-bd2 rounded-1 py-3 px-4 text-body-lg"
    >
      {(workspaces.data ?? []).map((w) => (
        <option key={w.workspace_id} value={w.workspace_id}>
          {w.name}
          {w.workspace_id === currentWorkspaceId ? ' (captured from)' : ''}
        </option>
      ))}
    </select>
  )
}
